import React, { useEffect, useRef, useState } from 'react';
import seedrandom from 'seedrandom';
import _, { find, flatten, isEqual, without } from 'lodash';
import { browserName, isMobile } from 'react-device-detect';
import { createPortal } from 'react-dom';
import { observeInlineDrag } from '../../../utilities/inline_drag_diagnostics';

interface Props {
  answer: any;
  prompt: any;
}

const DraggableInlineInputs: React.FC<Props> = (props) => {
  const [activeDraggableChoice, setActiveDraggableChoice] = useState(null);
  const activeDraggableChoiceRef = useRef(null);
  const choiceBankRef = useRef<HTMLDivElement>(null);

  // Drag and drop does not work well on mobile Safari (it thinsk you are selecting a word to look
  // it up). To handle that case we instead use a tap to select, then tap place in the blank
  // interaction. Update the question instructions to reflect this by using the mobile
  // instructions.
  useEffect(() => {
    if (!isMobile) return;

    const answerInstructions = document.querySelector('.answer-instructions');
    if (!answerInstructions) return;

    // Find and remove the child with "hidden-xs" class
    const hiddenElement = answerInstructions.querySelector('.hidden-xs');
    if (hiddenElement) {
      hiddenElement.remove();
    }

    // Find and remove the "visible-xs" class from the other span
    const visibleElement = answerInstructions.querySelector('.visible-xs');
    if (visibleElement) {
      visibleElement.classList.remove('visible-xs');
    }
  }, []);

  // Each blank is its own answer choice group, and a blank the student left empty has no
  // part saved for it. Look the part up by its group rather than pairing parts with
  // blanks in order, otherwise a partly filled in sentence collapses towards the first
  // blank when we render it back.
  const initializeValues = () => {
    const matchedChoiceIds = [];

    return props.prompt.answerChoiceGroups.map((answerChoiceGroup) => {
      const part = props.answer.parts.find((part) => (
        part.answerChoiceGroupId === answerChoiceGroup.id
      ));
      if (!part) return null;

      const choice = answerChoices.current.find((choice) => (
        choice.text === part.text && matchedChoiceIds.indexOf(choice.id) === -1
      ));
      if (choice) matchedChoiceIds.push(choice.id);

      return choice || null;
    });
  };

  // Use a seeded shuffle so that the answer choice order will randomized, but consistent
  const shuffleSeeded = (array, seed) => {
    seedrandom(seed, { global: true });
    const _lodash = _.runInContext();
    return _lodash.shuffle(array);
  };

  const answerChoices = useRef(
    shuffleSeeded(
      flatten(
        props.prompt.answerChoiceGroups.map((answerChoiceGroup) => (
          answerChoiceGroup.answerChoices
        ))
      ),
      `${props.prompt.id}`
    )
  );

  const [inlineInputs, setInlineInputs] = useState(null);
  const [values, setValues] = useState(initializeValues());

  useEffect(() => {
    if (isMobile || props.answer.isGraded || !inlineInputs || !choiceBankRef.current) return;
    return observeInlineDrag({
      bank: choiceBankRef.current,
      targets: Array.from(inlineInputs),
      choices: answerChoices.current,
      promptId: props.prompt.id,
      answerId: props.answer.id
    });
  }, [inlineInputs, props.prompt.id, props.answer.id, props.answer.isGraded]);

  useEffect(() => {
    activeDraggableChoiceRef.current = activeDraggableChoice;
  }, [activeDraggableChoice]);

  useEffect(() => {
    const targetInputs = document.getElementsByClassName('drag-target-inline');
    setInlineInputs(targetInputs);

    // Sometimes when dragging, the user might also inadvertently select some surrounding text.
    // All the selected text will go with the cursor as the user drags which is confusing visually.
    // Prevent this by not allowing text in the sentence to be selected.
    targetInputs[0].parentElement.style['user-select'] = 'none';
  }, []);

  useEffect(() => {
    const clearActiveDraggableChoice = (event) => {
      if (!activeDraggableChoiceRef.current) return;
      if (event.target.className === 'draggable-text') return;
      if (event.target.className === 'drag-target') return;

      setValues((currentValues) => (
        currentValues.map((value) => isEqual(value, activeDraggableChoiceRef.current) ? null : value)
      ));
      setActiveDraggableChoice(null);
    };

    document.addEventListener('click', clearActiveDraggableChoice);

    return () => {
      document.removeEventListener('click', clearActiveDraggableChoice);
    }
  }, []);

  const onValueChanged = (newValue, idx) => {
    // Remove the value from any previosuly assigned slots
    const updatedValues = values.map((value) => isEqual(newValue, value) ? null : value);
    updatedValues[idx] = newValue;
    setValues(updatedValues);
  };

  return (
    <>
      {
        props.prompt.answerChoiceGroups.map((answerChoiceGroup, idx) => {
          const inlineInput = (inlineInputs || [])[idx];

          return (
            <div key={idx} className="answer_input">
              {
                inlineInput && createPortal(
                  props.answer.isGraded ? (
                    <GradedInput
                      answerChoiceGroup={answerChoiceGroup}
                      value={values[idx]}
                    />
                  ) : (
                    <DragTargetInput
                      acceptPlace={!!activeDraggableChoice}
                      active={isEqual(values[idx], activeDraggableChoice)}
                      answerChoiceGroupId={answerChoiceGroup.id}
                      onChange={(newValue) => onValueChanged(newValue, idx)}
                      onTapToPlace={() => {
                        onValueChanged(activeDraggableChoice, idx);
                        setActiveDraggableChoice(null);
                      }}
                      onTapToSelect={() => setActiveDraggableChoice(values[idx])}
                      value={values[idx]}
                    />
                  ),
                  inlineInput
                )
              }
            </div>
          );
        })
      }

      <div
        ref={choiceBankRef}
        className="d-flex flex-wrap justify-content-center u-margin-V-l"
        style={{ columnGap: 10, rowGap: 5 }}
      >
        {
          answerChoices.current.map((choice: any) => (
            <DraggableText
              key={choice.id}
              active={isEqual(choice, activeDraggableChoice)}
              isDraggable={!props.answer.isGraded}
              inUse={!!values.find((value) => choice.id === value?.id)}
              onTapToSelect={() => setActiveDraggableChoice(choice)}
              value={choice}
            >
              {choice.text}
            </DraggableText>
          ))
        }
      </div>
    </>
  );
};

interface DraggableTextProps {
  active: boolean;
  isDraggable: boolean;
  inUse: boolean;
  onDragEnd?: (event) => void;
  onTapToSelect?: (event) => void;
  value: {
    id: number;
    text: string
  };
}

const DraggableText: React.FC<DraggableTextProps> = (props) => {
  let backgroundColor = 'inherit';
    if (props.inUse) {
      backgroundColor = 'rgb(239, 234, 231)';
    } else if (props.active) {
      backgroundColor = '#d3c7de';
    }

  const dragStart = (e) => {
    e.dataTransfer.setData('text/plain', JSON.stringify(props.value));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleClick = (event) => {
    if (useTapToSelect()) {
      props.onTapToSelect(event);
    }
  };

  const useTapToSelect = () => {
    return isMobile;
  };

  return (
    <span
      className="draggable-text"
      draggable={props.isDraggable && !useTapToSelect() && !props.inUse}
      onClick={handleClick}
      onDragEnd={props.onDragEnd}
      onDragStart={dragStart}
      style={{
        backgroundColor,
        cursor: props.isDraggable && !props.inUse ? 'move' : 'default',
        padding: '5px 10px'
      }}
    >
      {props.value.text}
    </span>
  );
};

interface DragTargetInputProps {
  acceptPlace: boolean;
  active: boolean;
  answerChoiceGroupId: number;
  onChange: (value) => void;
  onTapToPlace?: (event) => void;
  onTapToSelect?: (event) => void;
  value: {
    id: number;
    text: string
  };
}

const DragTargetInput: React.FC<DragTargetInputProps> = (props) => {
  const dragEnd = (e) => {
    if (e.dataTransfer.dropEffect === 'none') {
      props.onChange(null);
    }
  };

  const useTapToPlace = () => {
    return isMobile;
  };

  const onDrop =(e) => {
    props.onChange(JSON.parse(e.dataTransfer.getData('text/plain')));
  };

  return (
    <>
      <span
        className="drag-target"
        onClick={useTapToPlace() && props.acceptPlace ? props.onTapToPlace : () => {}}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        style={{
          borderBottom: '1px solid #4b504e',
          display: 'inline-block',
          marginBottom: 5,
          minHeight: 25,
          minWidth: props.value ? 'auto' : 136
        }}
      >
        {
          props.value ? (
            <DraggableText
              active={props.active}
              isDraggable={!useTapToPlace()}
              inUse={false}
              onDragEnd={dragEnd}
              onTapToSelect={props.onTapToSelect}
              value={props.value}
            />
          ) : (
            <span dangerouslySetInnerHTML={{ __html: '&nbsp;' }} />
          )
        }
      </span>

      {
        props.value && (
          <input
            name={`answer[parts_val][${props.answerChoiceGroupId}][text][]`}
            type="hidden"
            value={props.value.text}
          />
        )
      }
    </>
  )
};

interface GradedInputProps {
  answerChoiceGroup: any;
  value: {
    id: number;
    text: string
  };
}

const GradedInput: React.FC<GradedInputProps> = (props) => {
  const correctText = props.answerChoiceGroup.answerChoices.find((choice) => (
    choice.isCorrect
  )).text;
  const isCorrect = props.value?.text === correctText;

  return (
    <span
      style={{
        borderBottom: '1px solid #4b504e',
        display: 'inline-block',
        marginBottom: 5,
        minHeight: 25
      }}
    >
      {
        !isCorrect && (
          <span style={{ backgroundColor: '#9bdcae', fontWeight: 'bold' }}>
            {correctText}
          </span>
        )
      }

      <span
        style={{
          backgroundColor: isCorrect ? '#9bdcae' : '#ecacae',
          color: isCorrect ? 'inherit' : '#D9595D',
          fontWeight: 'bold',
          marginLeft: isCorrect ? 0 : 2,
          textDecoration: isCorrect ? 'none' : 'line-through'
        }}
      >
        {props.value?.text || <span dangerouslySetInnerHTML={{ __html: '&nbsp;&nbsp;&nbsp;&nbsp;' }} />}
      </span>
    </span>
  )
};

export default DraggableInlineInputs;
